import { err, Result } from "neverthrow";
import { DomainError } from "@/lib/api/domain-error";
import type { RequestFn } from "@/lib/api/types";
import {
  BillingOrderModel,
  CreateBillingModel,
  PurchaseStateModel,
} from "./models";

const BILLING_ROUTE_V1 = "v1/${account_slug}/billing";

export default class BillingRepository {
  constructor(private readonly request: RequestFn) {}

  async create(
    accountSlug: string,
    planId: string,
    cycle: "MONTHLY" | "YEARLY",
    customerData: unknown,
  ): Promise<Result<CreateBillingModel, DomainError>> {
    try {
      let url = `${BILLING_ROUTE_V1}/checkouts`;
      url = url.replace("${account_slug}", accountSlug);
      const apiResult = await this.request({
        url,
        method: "POST",
        data: { planId, cycle, customerData },
      });

      return apiResult.map(
        (domainData) => domainData.data as CreateBillingModel,
      );
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async getPurchaseState(
    accountSlug: string,
    planId: string,
    cycle: "MONTHLY" | "YEARLY",
  ): Promise<Result<PurchaseStateModel, DomainError>> {
    try {
      let url = `${BILLING_ROUTE_V1}/purchase-state`;
      url = url.replace("${account_slug}", accountSlug);
      url += `?planId=${encodeURIComponent(planId)}&cycle=${cycle}`;
      const result = await this.request({ url, method: "GET" });
      return result.map((domainData) => domainData.data as PurchaseStateModel);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async getOrder(
    accountSlug: string,
    orderId: string,
  ): Promise<Result<BillingOrderModel, DomainError>> {
    try {
      let url = `${BILLING_ROUTE_V1}/orders/${encodeURIComponent(orderId)}`;
      url = url.replace("${account_slug}", accountSlug);
      const result = await this.request({ url, method: "GET" });
      return result.map((domainData) => domainData.data as BillingOrderModel);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async cancelSubscription(
    accountSlug: string,
    subscriptionId: string,
  ): Promise<Result<void, DomainError>> {
    try {
      let url = `${BILLING_ROUTE_V1}/subscriptions/${subscriptionId}/cancel`;
      url = url.replace("${account_slug}", accountSlug);
      const apiResult = await this.request({
        url,
        method: "POST",
      });

      return apiResult.map(() => undefined);
    } catch (error) {
      return err(new DomainError(error));
    }
  }
}
